import * as React from "react"
import { Link } from "@inertiajs/react"
import { CircleAlert } from "lucide-react"
import { Field, fieldAria } from "@/components/admin/Field"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

// Pieces the admin forms share (M7): the error summary, the image picker with
// its preview, and the Save / Cancel row.

/** One line above a form that failed, pointing at the fields marked below. */
export function ErrorSummary({ errors }: { errors: Record<string, string | undefined> }) {
  const count = Object.values(errors).filter(Boolean).length
  if (count === 0) return null
  return (
    <div role="alert" className="callout callout-danger mt-6 flex items-start gap-3 py-3">
      <CircleAlert className="mt-1 h-4 w-4 shrink-0 text-danger-display" aria-hidden="true" />
      <p>
        {count === 1 ? "One field needs attention" : `${count} fields need attention`}. Nothing was saved yet.
      </p>
    </div>
  )
}

/**
 * A file field for an image (or a PDF, with `accept`): the current file's
 * preview, the chosen file's preview before it is uploaded, hint and error.
 */
export function ImagePicker({
  id,
  label,
  hint,
  currentUrl,
  currentAlt,
  file,
  error,
  onChange,
  accept = "image/png,image/jpeg,image/webp",
}: {
  id: string
  label: string
  hint: React.ReactNode
  currentUrl: string | null
  currentAlt: string
  file: File | null
  error?: string
  onChange: (file: File | null) => void
  accept?: string
}) {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)

  // A local preview of the chosen file; revoked when it changes or unmounts.
  React.useEffect(() => {
    if (!file) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const shown = previewUrl ?? currentUrl

  return (
    <div className="space-y-4">
      {shown && (
        <figure className="w-40">
          <div className="flex aspect-[3/4] items-center justify-center overflow-hidden rounded-md border border-hairline bg-surface">
            <img src={shown} alt={previewUrl ? `Preview of ${file?.name ?? "the chosen image"}` : currentAlt} className="h-full w-full object-contain" />
          </div>
          <figcaption className="mt-1 text-xs text-ink-muted">
            {previewUrl ? "New image, not saved yet" : "Current image"}
          </figcaption>
        </figure>
      )}
      <Field id={id} label={label} hint={hint} error={error}>
        <Input
          {...fieldAria(id, { error, hint })}
          type="file"
          accept={accept}
          className="form-control-large"
          onChange={(event) => onChange(event.target.files?.[0] ?? null)}
        />
      </Field>
    </div>
  )
}

/** Save and Cancel, at the foot of a form. */
export function FormActions({
  processing,
  submitLabel,
  cancelHref,
}: {
  processing: boolean
  submitLabel: string
  cancelHref: string
}) {
  return (
    <div className="mt-10 flex flex-col-reverse gap-2 border-t border-hairline pt-6 sm:flex-row sm:items-center">
      <Button asChild variant="ghost" size="lg">
        <Link href={cancelHref}>Cancel</Link>
      </Button>
      <Button type="submit" size="lg" disabled={processing}>
        {processing ? "Saving…" : submitLabel}
      </Button>
    </div>
  )
}
